"use client";

import { useCallback, useEffect, useState } from "react";
import { NUDGE_PUBLIC_CHAT_EVENT, type PublicChatNudge } from "@/lib/chat/open-public-chat";

const NUDGED_THIS_SESSION_KEY = "repackify:public-chat-nudged";
const DOCKED_PANEL_QUERY = "(min-width: 1024px)";

function nudgedThisSession(): boolean {
  try {
    return window.sessionStorage.getItem(NUDGED_THIS_SESSION_KEY) === "1";
  } catch {
    return true;
  }
}

function rememberNudge() {
  try {
    window.sessionStorage.setItem(NUDGED_THIS_SESSION_KEY, "1");
  } catch {
    return;
  }
}

function isNudge(value: unknown): value is PublicChatNudge {
  return (
    typeof value === "object" &&
    value !== null &&
    "line" in value &&
    typeof value.line === "string" &&
    "chip" in value &&
    typeof value.chip === "string" &&
    "prompts" in value &&
    Array.isArray(value.prompts) &&
    value.prompts.every((prompt: unknown) => typeof prompt === "string")
  );
}

export function useChatNudge({ busy, open }: { busy: boolean; open: () => void }) {
  const [nudge, setNudge] = useState<PublicChatNudge | null>(null);
  const [chipShown, setChipShown] = useState(false);
  const [autoOpened, setAutoOpened] = useState(false);

  useEffect(() => {
    const receive = (event: Event) => {
      if (!(event instanceof CustomEvent) || !isNudge(event.detail)) return;
      if (busy || nudgedThisSession()) return;
      rememberNudge();
      setNudge(event.detail);
      if (window.matchMedia(DOCKED_PANEL_QUERY).matches) {
        setAutoOpened(true);
        open();
      } else {
        setChipShown(true);
      }
    };
    window.addEventListener(NUDGE_PUBLIC_CHAT_EVENT, receive);
    return () => window.removeEventListener(NUDGE_PUBLIC_CHAT_EVENT, receive);
  }, [busy, open]);

  const openFromChip = useCallback(() => {
    setChipShown(false);
    open();
  }, [open]);

  const dismiss = useCallback(() => {
    setChipShown(false);
    setAutoOpened(false);
    setNudge(null);
  }, []);

  return { nudge, chipShown, autoOpened, openFromChip, dismiss };
}
